import React, { useState } from "react";
import {
  Offcanvas,
  Badge,
  Table,
  Row,
  Col,
  Button,
  Spinner,
} from "react-bootstrap";
import {
  IconPackage,
  IconPrinter,
} from "@tabler/icons-react";
import { format } from "date-fns";
import html2pdf from "html2pdf.js";
import Swal from "sweetalert2";

const StockTransferDetailsModal = ({ show, onHide, transfer, showPrice = false }) => {
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  const handlePrintPDF = async () => {
    if (!transfer) return;

    setIsGeneratingPDF(true);
    try {
      // Create print content
      const printContent = document.createElement("div");
      printContent.innerHTML = `
        <div style="font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; padding: 20px;">
          <!-- Compact Header: Logo + Transfer Info -->
          <div style="display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #000; padding-bottom: 15px; margin-bottom: 20px;">
            <!-- Logo Section -->
            <div style="flex: 0 0 auto;">
              <img src="/new-logo.jpeg" alt="Kuber Logo" style="width: 120px; height: auto; margin-bottom: 8px;" />
              <div style="font-size: 10px; color: #666; line-height: 1.3; max-width: 140px;">
                Br.Bayad, Desa Melinggih Kelod,<br/>
                Payangan, Gianyar, Bali 80572
              </div>
            </div>

            <!-- Transfer Info Section -->
            <div style="flex: 1; text-align: right; font-size: 13px;">
              <h4 style="margin: 0 0 8px 0; font-size: 16px;">Kwitansi Transfer Stok</h4>
              <div style="line-height: 1.4;">
                <strong>Nomor:</strong> ${transfer.transfer_number || ""}<br/>
                <strong>Status:</strong> <span style="background-color: ${
                  transfer.status?.toLowerCase() === "completed"
                    ? "#28a745"
                    : transfer.status?.toLowerCase() === "pending"
                    ? "#ffc107"
                    : transfer.status?.toLowerCase() === "cancelled"
                    ? "#dc3545"
                    : "#6c757d"
                }; color: white; padding: 1px 6px; border-radius: 3px; font-size: 10px;">
                  ${transfer.status || "Unknown"}
                </span><br/>
                <strong>Tanggal:</strong> ${formatDate(transfer.transfer_date)}
              </div>
            </div>
          </div>

          <!-- Compact Warehouse & Staff Information -->
          <div style="display: flex; gap: 15px; margin-bottom: 20px; flex-wrap: wrap;">
            <!-- Warehouse Info - Compact -->
            <div style="flex: 1; min-width: 200px; border: 1px solid #ccc; padding: 8px; font-size: 12px;">
              <div style="font-weight: bold; margin-bottom: 5px; color: #333;">📍 Gudang</div>
              <div><strong>Dari:</strong> ${transfer.from_warehouse?.name || "N/A"}</div>
              <div><strong>Ke:</strong> ${transfer.to_warehouse?.name || "N/A"}</div>
            </div>

            <!-- Staff Info - Compact -->
            <div style="flex: 1; min-width: 200px; border: 1px solid #ccc; padding: 8px; font-size: 12px;">
              <div style="font-weight: bold; margin-bottom: 5px; color: #333;">👤 Staff</div>
              <div><strong>Penanggung Jawab:</strong> ${transfer.responsible_staff?.name || "N/A"}</div>
              <div><strong>Pembuat:</strong> ${transfer.creator?.name || "N/A"}</div>
            </div>
          </div>

          ${transfer.notes ? `
          <!-- Notes Section -->
          <div style="margin-bottom: 20px; padding: 10px; border: 1px solid #ccc; font-size: 12px; border-radius: 4px;">
            <div style="font-weight: bold; margin-bottom: 5px; color: #333;">📝 Notes</div>
            <div style="white-space: pre-wrap;">${transfer.notes}</div>
          </div>
          ` : ''}

          <!-- Transfer Items -->
          <h3 style="margin-bottom: 15px; color: #333; border-bottom: 1px solid #ddd; padding-bottom: 5px;">Transfer Items</h3>
          <table style="width: 100%; border-collapse: collapse; margin-bottom: 30px;">
            <thead>
              <tr style="background-color: #f8f9fa;">
                <th style="border: 1px solid #ddd; padding: 8px; text-align: left; font-weight: bold;">Item Name</th>
                <th style="border: 1px solid #ddd; padding: 8px; text-align: left; font-weight: bold;">Inventory Name</th>
                <th style="border: 1px solid #ddd; padding: 8px; text-align: center; font-weight: bold;">Quantity</th>
                <th style="border: 1px solid #ddd; padding: 8px; text-align: center; font-weight: bold;">Unit</th>
                ${showPrice ? `
                <th style="border: 1px solid #ddd; padding: 8px; text-align: right; font-weight: bold;">Unit Price (Rp)</th>
                <th style="border: 1px solid #ddd; padding: 8px; text-align: right; font-weight: bold;">Subtotal (Rp)</th>
                ` : ''}
              </tr>
            </thead>
            <tbody>
              ${
                transfer.items
                  ?.map((item, index) => {
                    const price = parseFloat(item.inventory?.price || 0);
                    const quantity = parseFloat(item.quantity || 0);
                    const subtotal = price * quantity;
                    return `
                <tr ${index % 2 === 0 ? 'style="background-color: #f9f9f9;"' : ""}>
                  <td style="border: 1px solid #ddd; padding: 8px;">${item.item_name || `Item ${item.inventory_id}`}</td>
                  <td style="border: 1px solid #ddd; padding: 8px;">${item.inventory?.name || "N/A"}</td>
                  <td style="border: 1px solid #ddd; padding: 8px; text-align: center;">${item.quantity}</td>
                  <td style="border: 1px solid #ddd; padding: 8px; text-align: center;">${item.unit || "N/A"}</td>
                  ${showPrice ? `
                  <td style="border: 1px solid #ddd; padding: 8px; text-align: right;">${price.toLocaleString("id-ID")}</td>
                  <td style="border: 1px solid #ddd; padding: 8px; text-align: right;">${subtotal.toLocaleString("id-ID")}</td>
                  ` : ''}
                </tr>
              `;
                  })
                  .join("") || ""
              }
            </tbody>
            ${showPrice ? `
            <tfoot>
              <tr style="background-color: #e9ecef; font-weight: bold;">
                <td colspan="5" style="border: 1px solid #ddd; padding: 8px; text-align: right; font-weight: bold;">TOTAL:</td>
                <td style="border: 1px solid #ddd; padding: 8px; text-align: right; font-weight: bold;">${
                  transfer.items
                    ?.reduce((total, item) => {
                      const price = parseFloat(item.inventory?.price || 0);
                      const quantity = parseFloat(item.quantity || 0);
                      return total + price * quantity;
                    }, 0)
                    .toLocaleString("id-ID") || "0"
                }</td>
              </tr>
            </tfoot>
            ` : ''}
          </table>

          <!-- Footer -->
          <div style="margin-top: 50px; padding-top: 20px; border-top: 1px solid #ddd; text-align: center; color: #666; font-size: 12px;">
            <p>This document was generated on ${new Date().toLocaleDateString(
              "id-ID",
              {
                year: "numeric",
                month: "long",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              },
            )}</p>
            <p>KUBER - Workshop Management System</p>
          </div>
        </div>
      `;

      // Configure PDF options
      const opt = {
        margin: [0.5, 0.5, 0.5, 0.5], // top, left, bottom, right in inches
        filename: `Stock_Transfer_${transfer.transfer_number || "Unknown"}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: "#ffffff",
        },
        jsPDF: {
          unit: "in",
          format: "a4",
          orientation: "portrait",
        },
      };

      // Generate and download PDF
      await html2pdf().set(opt).from(printContent).save();

      Swal.fire(
        "Success!",
        "PDF has been generated and downloaded.",
        "success",
      );
    } catch (error) {
      console.error("Error generating PDF:", error);
      Swal.fire("Error!", "Failed to generate PDF.", "error");
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const receiptStyle = {
    border: "2px solid #000",
    padding: "20px",
    maxWidth: "800px",
    margin: "0 auto",
    fontFamily: "Arial, sans-serif",
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return format(new Date(dateString), "dd/MM/yyyy HH:mm");
    } catch {
      return dateString;
    }
  };

  const getStatusBadgeVariant = (status) => {
    switch (status?.toLowerCase()) {
      case "completed":
        return "success";
      case "pending":
        return "warning";
      case "cancelled":
        return "danger";
      default:
        return "secondary";
    }
  };

  if (!transfer) return null;

  return (
    <Offcanvas className="workshop-modal-scope" show={show} onHide={onHide} placement="end" style={{ width: '100%', maxWidth: '900px' }}>
      <Offcanvas.Header closeButton className="border-bottom bg-light">
        <Offcanvas.Title className="d-flex align-items-center h5 mb-0">
          <IconPackage className="me-2" size={20} />
          Stock Transfer Details
        </Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body className="p-0">
        <div className="p-3 p-md-4 mx-auto" style={{ maxWidth: "800px", fontFamily: "Arial, sans-serif" }}>
          {/* Header: Logo + Transfer Info */}
          <div className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center border-bottom border-dark pb-3 mb-4 gap-3">
            {/* Logo Section */}
            <div>
              <img
                src="/new-logo.jpeg"
                alt="Kuber Logo"
                style={{ width: "120px", height: "auto", marginBottom: "8px" }}
              />
              <div
                style={{
                  fontSize: "11px",
                  color: "#666",
                  lineHeight: "1.3",
                  maxWidth: "200px",
                }}
              >
                Br.Bayad, Desa Melinggih Kelod, Payangan, Gianyar, Bali 80572
              </div>
            </div>

            {/* Transfer Info Section */}
            <div className="text-sm-end text-start" style={{ fontSize: "14px" }}>
              <h4 className="mb-2 fw-bold" style={{ fontSize: "18px" }}>
                Kwitansi Transfer Stok
              </h4>
              <div style={{ lineHeight: "1.4" }}>
                <strong>Nomor:</strong> {transfer.transfer_number}
                <br />
                <strong>Status:</strong>{" "}
                <Badge
                  bg={getStatusBadgeVariant(transfer.status)}
                  style={{ fontSize: "11px" }}
                >
                  {transfer.status}
                </Badge>
                <br />
                <strong>Tanggal:</strong> {formatDate(transfer.transfer_date)}
              </div>
            </div>
          </div>

          {/* Warehouse & Staff Information */}
          <Row className="mb-4 g-3">
            {/* Warehouse Info */}
            <Col sm={6}>
              <div className="border p-2 rounded h-100" style={{ fontSize: "13px" }}>
                <div className="fw-bold mb-1 text-dark">
                  📍 Gudang
                </div>
                <div>
                  <strong>Dari:</strong> {transfer.from_warehouse?.name || "N/A"}
                </div>
                <div>
                  <strong>Ke:</strong> {transfer.to_warehouse?.name || "N/A"}
                </div>
              </div>
            </Col>

            {/* Staff Info */}
            <Col sm={6}>
              <div className="border p-2 rounded h-100" style={{ fontSize: "13px" }}>
                <div className="fw-bold mb-1 text-dark">
                  👤 Staff
                </div>
                <div>
                  <strong>Penanggung Jawab:</strong>{" "}
                  {transfer.responsible_staff?.name || "N/A"}
                </div>
                <div>
                  <strong>Pembuat:</strong> {transfer.creator?.name || "N/A"}
                </div>
              </div>
            </Col>
          </Row>

          {/* Notes Section */}
          {transfer.notes && (
            <div className="border p-2 rounded mb-4" style={{ fontSize: "13px" }}>
              <div className="fw-bold mb-1 text-dark">
                📝 Notes
              </div>
              <div style={{ whiteSpace: "pre-wrap" }}>{transfer.notes}</div>
            </div>
          )}

          {/* Items Table */}
          <div className="mb-4">
            <strong className="d-block mb-2">Item Transfer ({transfer.items?.length || 0}):</strong>
            {transfer.items && transfer.items.length > 0 ? (
              <div className="table-responsive">
                <Table bordered hover size="sm" className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th className="text-nowrap">Nama Item</th>
                      <th className="text-nowrap">Nama Inventori</th>
                      <th className="text-center text-nowrap">Jumlah</th>
                      <th className="text-center text-nowrap">Satuan</th>
                      {showPrice && (
                        <>
                          <th className="text-end text-nowrap">Harga</th>
                          <th className="text-end text-nowrap">Subtotal</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {transfer.items.map((item, index) => {
                      const price = parseFloat(item.inventory?.price || 0);
                      const quantity = parseFloat(item.quantity || 0);
                      const subtotal = price * quantity;

                      return (
                        <tr key={item.id || index}>
                          <td>{item.item_name || "N/A"}</td>
                          <td>{item.inventory?.name || "N/A"}</td>
                          <td className="text-center">{item.quantity || 0}</td>
                          <td className="text-center">{item.unit || "N/A"}</td>
                          {showPrice && (
                            <>
                              <td className="text-end text-nowrap">Rp {price.toLocaleString("id-ID")}</td>
                              <td className="text-end text-nowrap">Rp {subtotal.toLocaleString("id-ID")}</td>
                            </>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                  {showPrice && (
                    <tfoot className="table-light">
                      <tr>
                        <td
                          colSpan="4"
                          className="text-end fw-bold"
                        >
                          Total Keseluruhan:
                        </td>
                        <td colSpan="2" className="text-end fw-bold text-nowrap">
                          Rp{" "}
                          {transfer.items
                            .reduce((total, item) => {
                              const price = parseFloat(item.inventory?.price || 0);
                              const quantity = parseFloat(item.quantity || 0);
                              return total + price * quantity;
                            }, 0)
                            .toLocaleString("id-ID")}
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </Table>
              </div>
            ) : (
              <p className="text-muted">Tidak ada item dalam transfer ini</p>
            )}
          </div>

          {/* Signatures */}
          <Row className="mt-5 pt-3 pb-4">
            <Col xs={6} className="text-center">
              <div className="border-top border-dark pt-2 mx-auto" style={{ maxWidth: "200px", fontSize: "13px" }}>
                Tanda Tangan Pengirim
              </div>
            </Col>
            <Col xs={6} className="text-center">
              <div className="border-top border-dark pt-2 mx-auto" style={{ maxWidth: "200px", fontSize: "13px" }}>
                Tanda Tangan Penerima
              </div>
            </Col>
          </Row>
        </div>
      </Offcanvas.Body>
      <div className="border-top p-3  d-flex justify-content-end gap-2">
        <Button variant="secondary" onClick={onHide}>
          Tutup
        </Button>
        <Button
          variant="primary"
          onClick={handlePrintPDF}
          disabled={isGeneratingPDF}
          className="d-flex align-items-center gap-1"
        >
          {isGeneratingPDF ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
              />
              Generating PDF...
            </>
          ) : (
            <>
              <IconPrinter size={16} />
              Download PDF
            </>
          )}
        </Button>
      </div>
    </Offcanvas>
  );
};

export default StockTransferDetailsModal;
